import React from 'react';

/**
 * Wie React.lazy, aber mit `preload()`, das vor dem Hydratisieren abgewartet werden kann.
 * Ist das Modul geladen, rendert die Komponente synchron. Das ist nötig, weil das
 * vorgerenderte HTML sonst beim Hydratisieren durch den Suspense-Fallback ersetzt würde.
 */
export interface Preloadable<P> extends React.FC<P> {
  preload: () => Promise<void>;
}

export function preloadable<P extends object>(
  load: () => Promise<{ default: React.ComponentType<P> }>,
): Preloadable<P> {
  let Comp: React.ComponentType<P> | undefined;
  let laden: Promise<void> | undefined;
  const preload = () => (laden ??= load().then((m) => { Comp = m.default; }));
  const Wrapper = ((props: P) => {
    if (!Comp) throw preload();
    return <Comp {...props} />;
  }) as Preloadable<P>;
  Wrapper.preload = preload;
  return Wrapper;
}

/** Dieselbe Idee für Daten: `read()` liefert synchron oder suspendiert, bis `preload()` fertig ist. */
export function preloadableData<T>(load: () => Promise<T>) {
  let wert: T | undefined;
  let fertig = false;
  let laden: Promise<void> | undefined;
  const preload = () => (laden ??= load().then((w) => { wert = w; fertig = true; }));
  const read = (): T => {
    if (!fertig) throw preload();
    return wert as T;
  };
  return { preload, read };
}
